import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/Register.css";
import logo from "../assets/logo1.png";
import "bootstrap-icons/font/bootstrap-icons.css";
import Navbar from "../components/Navbar";

const Register = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    dob: "",
    gender: "",
    academic_level: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 1;
    return strength;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      setPasswordStrength(checkPasswordStrength(value));
      setPasswordMatch(value === form.confirmPassword);
    }
    if (name === "confirmPassword") {
      setPasswordMatch(value === form.password);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    // Validaciones básicas
    if (!form.name.trim()) {
      setError("Por favor, introduce tu nombre.");
      return;
    }
    if (!form.email.includes("@")) {
      setError("Por favor, introduce un email válido.");
      return;
    }
    if (form.password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (!passwordMatch) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!form.dob) {
      setError("Por favor, selecciona tu fecha de nacimiento.");
      return;
    }
    if (!form.gender) {
      setError("Por favor, selecciona tu género.");
      return;
    }
    if (!form.academic_level) {
      setError("Por favor, selecciona tu grado académico.");
      return;
    }
    if (!acceptedPrivacy) {
      setError("Debes aceptar el Aviso de Privacidad para continuar.");
      return;
    }

    // Payload (lista blanca)
    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      dob: form.dob, // formato YYYY-MM-DD
      gender: form.gender,
      academic_level: form.academic_level,
      // Si decides guardar consentimiento en backend, agrega:
      // privacyConsent: { accepted: true, version: "2025-08-22", acceptedAt: new Date().toISOString() }
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/register`, payload);
      setMessage("Usuario registrado con éxito. Redirigiendo...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Error en el registro. Intenta de nuevo.");
      console.warn("Error en el registro:", err);
    }
  };

  useEffect(() => {
    // Cargar JS de Bootstrap para modales
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <Navbar />

      <div className="register-container">
        <div className="register-card">
          <img src={logo} alt="Logo" style={{ height: 48, marginBottom: 12 }} />
          <h2 className="register-title">Registro</h2>

          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}

          <form onSubmit={handleSubmit}>
            <h4 className="section-title">Datos personales</h4>

            <div className="mb-3">
              <input
                type="text"
                name="name"
                className="form-control"
                placeholder="Nombre Completo"
                value={form.name}
                onChange={handleChange}
                required
                autoComplete="name"
              />
            </div>

            <div className="mb-3">
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder="Correo Electrónico"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            <div className="mb-3">
              <input
                type="password"
                name="password"
                className="form-control"
                placeholder="Contraseña (mínimo 6 caracteres)"
                value={form.password}
                onChange={handleChange}
                required
                minLength={6}
                autoComplete="new-password"
              />
              <div className="password-strength-meter mt-2">
                <div className="strength-bars">
                  <div className={`strength-bar ${passwordStrength >= 1 ? "active" : ""}`}></div>
                  <div className={`strength-bar ${passwordStrength >= 2 ? "active" : ""}`}></div>
                  <div className={`strength-bar ${passwordStrength >= 3 ? "active" : ""}`}></div>
                  <div className={`strength-bar ${passwordStrength >= 4 ? "active" : ""}`}></div>
                </div>
                <small className="strength-text">
                  {passwordStrength === 0 && "Muy débil"}
                  {passwordStrength === 1 && "Débil"}
                  {passwordStrength === 2 && "Media"}
                  {passwordStrength === 3 && "Fuerte"}
                  {passwordStrength === 4 && "Muy fuerte"}
                </small>
              </div>
            </div>

            <div className="mb-3">
              <input
                type="password"
                name="confirmPassword"
                className={`form-control ${
                  form.confirmPassword ? (passwordMatch ? "is-valid" : "is-invalid") : ""
                }`}
                placeholder="Confirmar contraseña"
                value={form.confirmPassword}
                onChange={handleChange}
                required
                minLength={6}
                autoComplete="new-password"
              />
              {form.confirmPassword && !passwordMatch && (
                <div className="invalid-feedback">Las contraseñas no coinciden</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="dob" className="form-label">Fecha de Nacimiento</label>
              <input
                type="date"
                name="dob"
                id="dob"
                className="form-control"
                value={form.dob}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <select
                name="gender"
                className="form-select"
                value={form.gender}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona tu género</option>
                <option value="Masculino">Masculino</option>
                <option value="Femenino">Femenino</option>
                <option value="Otro">Otro</option>
                <option value="Prefiero no decirlo">Prefiero no decirlo</option>
              </select>
            </div>

            <div className="mb-3">
              <select
                name="academic_level"
                className="form-select"
                value={form.academic_level}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona un grado académico</option>
                <option value="Secundaria">Secundaria</option>
                <option value="Media Superior">Media Superior</option>
                <option value="Superior">Superior</option>
              </select>
            </div>

            {/* Consentimiento de privacidad */}
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                id="privacyCheck"
                className="form-check-input"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                required
              />
              <label className="form-check-label" htmlFor="privacyCheck">
                He leído y acepto el{" "}
                <a
                  href="#"
                  data-bs-toggle="modal"
                  data-bs-target="#privacyModal"
                  onClick={(e) => e.preventDefault()}
                >
                  Aviso de Privacidad
                </a>.
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-register w-100 d-flex justify-content-center align-items-center"
              disabled={!acceptedPrivacy}
            >
              Registrarse
            </button>

          </form>

          <div className="register-footer mt-3">
            <p>
              ¿Ya tienes cuenta? <Link to="/login">Inicia sesión aquí</Link>
            </p>
          </div>
        </div>
      </div>

      {/* Modal Aviso de Privacidad */}
      <div
        className="modal fade"
        id="privacyModal"
        tabIndex="-1"
        aria-labelledby="privacyModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="privacyModalLabel">Aviso de Privacidad</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
            </div>
            <div className="modal-body">
              {/* ⚠️ Reemplaza este contenido con tu aviso real */}
              <p><strong>1.- IDENTIDAD Y DOMICILIO DEL RESPONSABLE:</strong> El responsable del tratamiento de sus datos personales es Mirai Innovation, con domicilio en Risco 817, Colonia Playas de Tijuana Sección Jardines, Tijuana, México, CP:22500. En lo sucesivo, “el Responsable”.</p>

              <p><strong>2.- ⁠DATOS PERSONALES QUE RECABAMOS:</strong> Para fines de la orientación vocacional y la prestación de servicios educativos, podemos recabar los siguientes datos personales: </p>
              <p>•	Datos de identificación (nombre, apellidos, edad)</p>
              <p>•	Datos de contacto (correo electrónico)</p>
              <p>•	Datos académicos (escuela de procedencia, promedio, resultados de exámenes, intereses académicos y vocacionales).</p>
              <p>•	Datos de uso y navegación en nuestra plataforma (direcciones IP, cookies y otros identificadores electrónicos).</p> 

              <p><strong>3.- FINALIDADES DEL TRATAMIENTO:</strong> La información que usted proporcione será utilizada para: </p>
              <p>•	Proveer y administrar el servicio de orientación vocacional y asesoría educativa.</p>
              <p>•	Evaluar y analizar los resultados de los cuestionarios para recomendar carreras y programas de estudio.</p>
              <p>•	Comunicarnos con usted para ofrecerle información relevante sobre programas, becas, y actividades relacionadas.</p>
              <p>•	Mejorar nuestros servicios y la experiencia en la plataforma.</p> 
              <p>•	Cumplir con obligaciones legales y administrativas.</p> 

              <p><strong>4.- TRANSFERENCIA DE DATOS:</strong> Sus datos personales no serán vendidos, alquilados ni compartidos con terceros para fines comerciales sin su consentimiento. Podremos transferir sus datos a: </p>
              <p>•	Instituciones educativas, cuando sea necesario para el proceso de orientación y admisión, siempre que sea estrictamente para los fines señalados.</p>
              <p>•	Proveedores de servicios que colaboren en el mantenimiento y mejora de nuestra plataforma, quienes estarán obligados a resguardar su información conforme a este aviso de privacidad.</p>

              <p><strong>5.- DERECHOS ARCO:</strong> Usted tiene derecho a conocer, actualizar, rectificar y, en su caso, cancelar sus datos personales, así como a oponerse al tratamiento de los mismos o revocar el consentimiento que para tal fin nos haya otorgado, mediante los procedimientos que hemos implementado. Para ello, puede enviar una solicitud al correo electrónico [correo@dominio.com] o ponerse en contacto con nuestro Departamento de Protección de Datos en el domicilio señalado.</p>

              <p><strong>6.- MEDIDAS DE SEGURIDAD:</strong> El Responsable adopta las medidas de seguridad administrativas, técnicas y físicas necesarias para proteger sus datos personales contra daño, pérdida, alteración, acceso no autorizado o uso indebido. Nos comprometemos a mantener la confidencialidad de su información y a actualizar nuestras medidas de seguridad conforme a los avances tecnológicos.</p>
              
              <p><strong>7.- CAMBIOS AL AVISO DE PRIVACIDAD:</strong> El presente aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos legales o de nuestras prácticas internas. Cualquier cambio será comunicado oportunamente a través de [medio de comunicación, sitio web, correo electrónico, etc.].</p>

              <p><strong>8.- CONTACTO:</strong> Si tiene dudas o comentarios sobre este aviso de privacidad, o desea ejercer sus derechos ARCO, puede ponerse en contacto con nosotros a través del siguiente medio: </p>
              <p>•	Correo electrónico: [correo@dominio.com]</p>
              <p>•	Teléfono: [número de contacto]</p>

              <p><strong>Aviso de Consentimiento - MIRAI ABILITY LAB®</strong> Al continuar, aceptas que:</p>
              <p>•	Tu participación en los cuestionarios es voluntaria.</p>
              <p>•	Tus respuestas se usarán solo con fines académicos y de diagnóstico educativo.</p>
              <p>•	Tus datos personales estarán protegidos conforme a la ley y no serán compartidos con terceros.</p>
              <p>•	La información se mantendrá confidencial y se usará únicamente para generar reportes académicos y estadísticos.</p>
              <p> Con tu aceptación contribuyes a mejorar tu aprendizaje, prevenir riesgos y apoyar la creación de nuevas políticas educativas.</p>
              
              <p><strong> El presente aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos legales o de nuestras prácticas internas. Cualquier cambio será comunicado oportunamente a través de [medio de comunicación, sitio web, correo electrónico, etc.].</strong></p>

              <p><em>Última actualización: 22/08/2025</em></p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                Cerrar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
                onClick={() => setAcceptedPrivacy(true)}
              >
                Acepto
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
